// HouseholdsPage — /households: one row per household, applications included.
//
//   Miles (Q10): "One household list page… Applications and Households become
//   one list; the AppFolio relationship is a column/filter, not a separate
//   page." And (Q7): "This is contextual to our interactions with the
//   household… Not just stupidly creating a page that makes sense in one case
//   but then is useless in another instance."
//
// So there is no application list and no household list — there is a list of
// households, and what we happen to be to each other (`relationship`) is a
// column you can filter on. A household whose entire history with us is one
// renewal shows one renewal. A household that is still an application, with
// nothing sent, is a NORMAL ROW (T15) — that is where "New Lease" starts, and a
// list that hid it would hide the front door.
//
// Two rules this page will not break:
//
//   1. IT DERIVES NOTHING. `standing.line` is computed server-side over the
//      rows THIS VIEWER may see (§8.4, T5) and rendered verbatim. A client that
//      recomputed a standing line would compute it over a filtered set and
//      quietly contradict the API about what is happening to a household — and
//      on an RRA household it would contradict it in the one direction that
//      matters.
//   2. IT ORDERS AND FILTERS NOTHING. `/api/households` takes the filter, the
//      search and the relationship as params (T27 — they used to be client-side
//      over an unparameterised endpoint); what arrives is what is shown.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRealtime } from '@/hooks/useRealtime';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError } from '@/lib/api';
import { OcEmpty, OcError, OcLoading, SignalChips } from '@/components/oc/bits';
import { describeWhere, describeWithheldKinds } from '@/lib/viewDocs';
import { absoluteTime, relativeTime } from '@/components/sentItems/sentItemsView';
import { formatWhen } from '@/lib/commandCenterClient';
import {
  HOUSEHOLD_FILTERS,
  HOUSEHOLD_FILTER_HINT,
  HOUSEHOLD_FILTER_LABEL,
  HOUSEHOLD_RELATIONSHIPS,
  RELATIONSHIP_LABEL,
  RELATIONSHIP_TONE,
  applyHouseholdParamsToSearch,
  fetchHouseholds,
  hasActiveHouseholdFilters,
  householdParamsFromSearch,
  householdPath,
  householdsQuery,
  sendHubPathFor,
  standingDotTone,
  type HouseholdsFilterPatch,
} from '@/lib/householdsClient';
import type {
  HouseholdListResponse,
  HouseholdListRow,
  HouseholdRelationship,
  HouseholdsFilter,
} from '@/lib/sentItemsClient';
import { fixtureHouseholdListResponse } from '@/lib/householdsFixtures';

function listError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    return body?.message ?? body?.error ?? `Couldn't load the households (${err.status}).`;
  }
  return err instanceof Error ? err.message : "Couldn't load the households.";
}

/** True when the event target is a place where typing should win over j/k. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

// ---------------------------------------------------------------------------
// Row actions
// ---------------------------------------------------------------------------

/**
 * The row's actions (§8.2).
 *
 * THE LEASE CTA SAYS WHAT IS TRUE OF THIS HOUSEHOLD (sol finding 10). The
 * Applications page this list replaced had four of them — New Lease / Resume
 * Draft (+ "Never generated" or "Unsaved changes") / View Lease PDF + Edit
 * Draft — and collapsing them into one always-on "Lease builder" made the same
 * click mean "start something" on one row and "you are mid-draft" on the next.
 * The label, the badge and the destination now come from `row.lease`, which the
 * server derives from the draft and the lease item; this component chooses
 * nothing (rule 1 above applies to CTAs as much as to standing lines).
 *
 * "Send…" carries the household into the hub (`?householdId=…`), so the surface
 * it opens can preselect them instead of dropping staff on an empty picker.
 *
 * §8.2 says "menu"; the items rendered flat are what that menu would contain,
 * one click sooner, and they stay visible to a keyboard and to a test. Noted as
 * a deliberate deviation.
 */
function RowActions({ row }: { row: HouseholdListRow }): React.JSX.Element {
  return (
    <span style={{ display: 'inline-flex', gap: 'var(--space-2)', flexWrap: 'nowrap' }}>
      {row.lease.href && (
        <Link className="btn" data-variant="secondary" data-size="sm" to={row.lease.href}>
          {row.lease.label}
        </Link>
      )}
      {row.lease.secondaryHref && row.lease.secondaryLabel && (
        <Link className="btn" data-variant="ghost" data-size="sm" to={row.lease.secondaryHref}>
          {row.lease.secondaryLabel}
        </Link>
      )}
      <Link className="btn" data-variant="ghost" data-size="sm" to={sendHubPathFor(row)}>
        Send…
      </Link>
    </span>
  );
}

// ---------------------------------------------------------------------------
// The intake / verification chips
// ---------------------------------------------------------------------------

/**
 * What this household still owes us, and what came back on it.
 *
 * Both chips are QUIET by design and both are silent when there is nothing to
 * say: a household nobody has asked anything of renders neither, exactly as the
 * old badges did (`FormStatusBadge` returned null at `total === 0`). The point
 * of a chip here is that a row which needs a person LOOKS different from one
 * that does not — a chip on every row would restore the uniformity this is
 * fixing.
 *
 * "Verification concern" is the only one that takes a warning tone, and it is
 * still advisory: it gates nothing, exactly as it gated nothing on the
 * Applications page.
 */
export function HouseholdChips({ row }: { row: HouseholdListRow }): React.JSX.Element | null {
  const { intake, verification, lease } = row;
  const showIntake = intake.requested > 0;
  const showLeaseNote = Boolean(lease.note);
  if (!showIntake && !verification.label && !showLeaseNote) return null;

  return (
    <span
      style={{
        display: 'inline-flex',
        gap: 'var(--space-2)',
        flexWrap: 'wrap',
        marginTop: 'var(--space-1)',
      }}
    >
      {showIntake && (
        <span
          className="pill"
          data-tone={
            intake.state === 'complete' ? 'success' : intake.stale ? 'warning' : 'neutral'
          }
          title={
            intake.state === 'complete'
              ? 'Every intake section this household was asked for is in.'
              : intake.stale
                ? 'An intake section has been outstanding for more than three days.'
                : 'Intake sections asked for, and how many are in.'
          }
        >
          {intake.stale && <span className="dot" data-tone="warning" />}
          {intake.label}
        </span>
      )}
      {verification.label && (
        <span
          className="pill"
          data-tone={
            verification.concern && !verification.cleared
              ? 'danger'
              : verification.concern
                ? 'neutral'
                : 'info'
          }
          title={
            verification.concern
              ? verification.cleared
                ? 'A rental verification came back with red flags or a concerns verdict, and staff have marked it reviewed. Advisory — it blocks nothing.'
                : 'A rental verification came back with red flags or a concerns verdict. Advisory — it blocks nothing.'
              : 'Verification requests sent to a previous landlord who has not answered yet.'
          }
        >
          {verification.label}
        </span>
      )}
      {/* The badge that used to sit beside Resume Draft. */}
      {showLeaseNote && (
        <span className="badge" data-size="sm">
          {lease.note}
        </span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// The table
// ---------------------------------------------------------------------------

export interface HouseholdsTableProps {
  /** Rendered in the order given. This component never sorts (T27). */
  rows: HouseholdListRow[];
  onOpen: (householdId: string) => void;
  /** Keyboard cursor, owned by the page so ↑/↓ and the click agree. */
  activeIndex?: number;
  rowRef?: (index: number, el: HTMLTableRowElement | null) => void;
}

/**
 * One row per household (§8.2): who · where · what we are to each other ·
 * WHERE IT STANDS · how much is moving · signals · when it last moved — plus,
 * under the name, where its intake and its verifications stand.
 *
 * Exported separately from the page because everything worth asserting about
 * it is a function of `rows` alone — a page that fetches renders "Loading…"
 * under react-dom/server and is therefore untestable.
 */
export function HouseholdsTable({
  rows,
  onOpen,
  activeIndex,
  rowRef,
}: HouseholdsTableProps): React.JSX.Element {
  return (
    <div className="table-wrap si-table-wrap">
      <table className="table si-table">
        <thead>
          <tr>
            <th scope="col">Household</th>
            <th scope="col">Relationship</th>
            <th scope="col">Where it stands</th>
            <th scope="col">In motion</th>
            <th scope="col">Signals</th>
            <th scope="col">Last activity</th>
            <th scope="col" className="col-action">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const where = describeWhere(row.propertyLabel, row.unitLabel);
            return (
              <tr
                key={row.householdId}
                className="si-row"
                ref={(el) => rowRef?.(i, el)}
                data-active={i === activeIndex ? 'true' : undefined}
                data-blocked={row.standing.tone === 'blocked' ? 'true' : undefined}
                data-clickable="true"
                onClick={() => onOpen(row.householdId)}
              >
                <td data-label="Household">
                  <span className="si-subject">{row.name}</span>
                  {where && <span className="si-where">{where}</span>}
                  {/* What this household still owes us, and what came back on
                      it — the operational overview the Applications page had
                      (sol finding 10). Silent when there is nothing to say. */}
                  <HouseholdChips row={row} />
                </td>
                <td data-label="Relationship">
                  <span className="pill" data-tone={RELATIONSHIP_TONE[row.relationship]}>
                    {RELATIONSHIP_LABEL[row.relationship]}
                  </span>
                </td>
                {/* Verbatim from the API (§8.4). No stage, no next step, and
                    nothing this page decided. */}
                <td data-label="Where it stands">
                  <span className="si-standing" data-tone={row.standing.tone}>
                    <span className="dot" data-tone={standingDotTone(row.standing.tone)} />
                    {row.standing.line}
                  </span>
                </td>
                <td data-label="In motion">{row.inMotionCount > 0 ? row.inMotionCount : '—'}</td>
                <td data-label="Signals">
                  <SignalChips counts={row.signals} />
                </td>
                <td data-label="Last activity">
                  <span title={absoluteTime(row.lastActivityAt)}>
                    {relativeTime(row.lastActivityAt)}
                  </span>
                </td>
                <td className="col-action" onClick={(e) => e.stopPropagation()}>
                  <RowActions row={row} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

export function HouseholdsPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Dev-only: render the §7.4 fixture corpus with no API at all, so the page is
  // reviewable while W3 is still writing the route it calls. It does NOT filter
  // or sort the fixtures — a fixtures mode that quietly sorted would hide the
  // very bug rule 2 above exists to prevent.
  const fixturesMode = Boolean(import.meta.env?.DEV) && searchParams.get('fixtures') === '1';

  // Memoised on the WIRE QUERY rather than on the URL object, so a change that
  // does not affect the request (a future ?open=) cannot re-fetch the list.
  const listQuery = householdsQuery(householdParamsFromSearch(searchParams));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const params = useMemo(() => householdParamsFromSearch(searchParams), [listQuery]);
  const filter: HouseholdsFilter = params.filter ?? 'motion';

  const [data, setData] = useState<HouseholdListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const liveRef = useRef(0);

  // Live refresh (restored by the integrator, 2026-08-29): the Applications
  // page this list replaced was the one screen wired to the realtime feed, so
  // a lease signing or an intake landing showed up without a reload. A
  // realtime message bumps `tick` on a 1.5 s trailing edge (a signing burst is
  // one refetch, not ten); a tick refetches SILENTLY — the table stays put,
  // no spinner — because nothing the user did asked for a loading state.
  const [tick, setTick] = useState(0);
  const tickTimer = useRef<number | undefined>(undefined);
  useRealtime(() => {
    if (fixturesMode || tickTimer.current !== undefined) return;
    tickTimer.current = window.setTimeout(() => {
      tickTimer.current = undefined;
      setTick((t) => t + 1);
    }, 1500);
  });
  const seenTick = useRef(0);

  useEffect(() => {
    if (fixturesMode) {
      setData(fixtureHouseholdListResponse());
      setLoading(false);
      setError(null);
      return;
    }
    const token = ++liveRef.current;
    const silent = seenTick.current !== tick;
    seenTick.current = tick;
    if (!silent) setLoading(true);
    setError(null);
    void (async () => {
      try {
        const res = await fetchHouseholds(params);
        if (liveRef.current !== token) return;
        setData(res);
      } catch (err) {
        if (liveRef.current !== token) return;
        setError(listError(err));
      } finally {
        if (liveRef.current === token) setLoading(false);
      }
    })();
  }, [params, fixturesMode, tick]);

  const updateParams = useCallback(
    (mutate: (p: URLSearchParams) => void) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      });
    },
    [setSearchParams],
  );

  const patchParams = useCallback(
    (patch: HouseholdsFilterPatch) => {
      updateParams((p) => {
        applyHouseholdParamsToSearch(p, { ...householdParamsFromSearch(p), ...patch });
      });
    },
    [updateParams],
  );

  // The search box is the one control with local state: the URL updates on a
  // 200ms trailing edge, so a five-letter name is one history entry, not five.
  const [q, setQ] = useState(params.q ?? '');
  useEffect(() => {
    setQ(params.q ?? '');
  }, [params.q]);
  useEffect(() => {
    if (q === (params.q ?? '')) return;
    const t = setTimeout(() => patchParams({ q }), 200);
    return () => clearTimeout(t);
  }, [q, params.q, patchParams]);

  const rows = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? rows.length;
  const withheldNote = describeWithheldKinds(data?.withheld ?? []);
  const filtered = hasActiveHouseholdFilters(params);

  // Keyboard, the same model the Sent Items list uses: ↑/↓ and j/k move, Enter
  // opens. Learned once, works on both lists.
  const [activeIndex, setActiveIndex] = useState(0);
  const rowRefs = useRef<(HTMLTableRowElement | null)[]>([]);
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(0, rows.length - 1)));
  }, [rows.length]);
  useEffect(() => {
    rowRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(e.target)) return;
      if (rows.length === 0) return;
      const row = rows[activeIndex];
      if (e.key === 'ArrowDown' || e.key === 'j') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, rows.length - 1));
      } else if (e.key === 'ArrowUp' || e.key === 'k') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        if (!row) return;
        e.preventDefault();
        navigate(householdPath(row.householdId));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [rows, activeIndex, navigate]);

  return (
    <div className="oc-page si-page">
      <header className="oc-page-header">
        <h1 className="oc-page-title">Households</h1>
        <span className="oc-meta">
          Everyone we are working with, and where each one stands.
          {data?.generatedAt ? ` As of ${formatWhen(data.generatedAt)}.` : ''}
        </span>
      </header>

      <div className="si-controls">
        <div
          className="tabs si-views"
          data-variant="segmented"
          role="tablist"
          aria-label="Which households to show"
        >
          {HOUSEHOLD_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              className="tab"
              role="tab"
              aria-selected={f === filter}
              onClick={() => patchParams({ filter: f })}
            >
              {HOUSEHOLD_FILTER_LABEL[f]}
              <span className="si-view-hint">{HOUSEHOLD_FILTER_HINT[f]}</span>
            </button>
          ))}
        </div>
        <span className="oc-meta si-count">
          {rows.length === total
            ? `${total} household${total === 1 ? '' : 's'}`
            : `${rows.length} of ${total}`}
        </span>
      </div>

      <div className="filter-bar si-filters">
        <input
          type="search"
          className="input si-search"
          placeholder="Search person, property or address"
          aria-label="Search households by person, property or address"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select
          className="input si-state-select"
          aria-label="Filter by relationship"
          value={params.relationship?.join(',') ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            patchParams({
              relationship: v ? (v.split(',') as HouseholdRelationship[]) : undefined,
            });
          }}
        >
          <option value="">Any relationship</option>
          {/* Applying and Approved are BOTH active applications — an approved
              group AppFolio hasn't converted is the lease-building window, and
              hiding it under "Applying" is what confused everyone (WEL-147). */}
          <option value="applying,approved">Applicants (applying or approved)</option>
          {HOUSEHOLD_RELATIONSHIPS.map((r) => (
            <option key={r} value={r}>
              {RELATIONSHIP_LABEL[r]}
            </option>
          ))}
        </select>
        {filtered && (
          <button
            type="button"
            className="btn"
            data-variant="ghost"
            data-size="sm"
            onClick={() =>
              updateParams((p) => {
                for (const key of ['filter', 'relationship', 'q']) p.delete(key);
              })
            }
          >
            Clear filters
          </button>
        )}
      </div>

      {/* A standing statement of scope, not a notification — the same posture
          the Docs list and the household file take (C9). It is unconditional
          for an off-roster viewer, so it says nothing about whether this list
          would have been any longer. */}
      {withheldNote && <div className="oc-meta si-withheld">{withheldNote}</div>}

      {error && <OcError>{error}</OcError>}

      {loading ? (
        <OcLoading label="Loading households…" />
      ) : rows.length === 0 ? (
        <OcEmpty>
          {filtered
            ? 'No households match those filters.'
            : filter === 'motion'
              ? 'Nothing is in motion. Switch to “All” to see every household.'
              : filter === 'closed'
                ? 'No closed households yet.'
                : 'No households yet.'}
        </OcEmpty>
      ) : (
        <HouseholdsTable
          rows={rows}
          activeIndex={activeIndex}
          onOpen={(id) => navigate(householdPath(id))}
          rowRef={(i, el) => {
            rowRefs.current[i] = el;
          }}
        />
      )}
    </div>
  );
}
